import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Trash2, Settings2, Plus, Pencil, Users, CopyPlus } from "lucide-react";
import { toast } from "sonner";
import BulkShiftAssignDialog from "@/components/app/BulkShiftAssignDialog";
import { useProperty } from "@/context/PropertyContext";
import { COLOR_OPTIONS, COLOR_CLASSES } from "@/lib/shiftColors";

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function weekDays(anchor) {
  const start = new Date(anchor);
  start.setDate(anchor.getDate() - anchor.getDay()); // Sunday
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export default function ShiftScheduler() {
  const { user } = useAuth();
  const { selectedId } = useProperty();
  const [anchor, setAnchor] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", user_name: "", date: "", type: "morning", start_time: "06:00", end_time: "14:00", note: "" });
  const [tplOpen, setTplOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const days = useMemo(() => weekDays(anchor), [anchor]);
  const dateFrom = fmtDate(days[0]);
  const dateTo = fmtDate(days[days.length - 1]);
  const isManager = ["super_admin", "general_manager", "property_admin", "supervisor"].includes(user?.role);

  const templateByKey = useMemo(() => {
    const m = {};
    for (const t of templates) m[t.key] = t;
    return m;
  }, [templates]);

  const load = useCallback(async () => {
    const reqs = [
      api.get(`/shifts?date_from=${dateFrom}&date_to=${dateTo}`),
      api.get(`/shift-templates`),
    ];
    if (isManager) reqs.push(api.get("/users"));
    const results = await Promise.all(reqs);
    setShifts(results[0].data);
    setTemplates(results[1].data);
    if (isManager) setUsers(results[2].data);
    else setUsers([user]);
  }, [dateFrom, dateTo, isManager, user]);

  useEffect(() => { load(); }, [load]);

  const openCell = (u, d) => {
    if (!isManager) return;
    const existing = shifts.find((s) => s.user_id === u.id && s.date === fmtDate(d));
    const firstTpl = templates[0] || { key: "morning", start_time: "06:00", end_time: "14:00" };
    setForm({
      user_id: u.id,
      user_name: u.name,
      date: fmtDate(d),
      type: existing?.type || firstTpl.key,
      start_time: existing?.start_time || firstTpl.start_time,
      end_time: existing?.end_time || firstTpl.end_time,
      note: existing?.note || "",
      existing_id: existing?.id || null,
    });
    setOpen(true);
  };

  const applyTemplate = (key) => {
    const tpl = templateByKey[key];
    if (!tpl) return setForm((f) => ({ ...f, type: key }));
    setForm((f) => ({ ...f, type: key, start_time: tpl.start_time, end_time: tpl.end_time }));
  };

  const submit = async () => {
    try {
      await api.post("/shifts", {
        user_id: form.user_id,
        date: form.date,
        type: form.type,
        start_time: form.start_time,
        end_time: form.end_time,
        note: form.note || null,
      });
      toast.success("Shift saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async () => {
    if (!form.existing_id) return;
    if (!window.confirm("Remove this shift?")) return;
    await api.delete(`/shifts/${form.existing_id}`);
    toast.success("Shift removed");
    setOpen(false);
    load();
  };

  const copyPrevWeek = async () => {
    const target = fmtDate(days[0]);
    const srcD = new Date(days[0]); srcD.setDate(srcD.getDate() - 7);
    const source = fmtDate(srcD);
    if (!window.confirm(`Copy last week's roster (week of ${source}) into this week (${target})?\nExisting shifts are kept — only empty days get filled.`)) return;
    try {
      const { data } = await api.post("/shifts/copy-week", { source_week_start: source, target_week_start: target, property_id: selectedId || null });
      toast.success(`Copied ${data.created} shift(s)${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Copy failed");
    }
  };

  // Per-day coverage counts (total + per shift type) for the footer summary.
  const coverageByDay = useMemo(() => {
    const map = {};
    for (const s of shifts) {
      const m = map[s.date] || (map[s.date] = { total: 0, types: {} });
      m.total += 1;
      m.types[s.type] = (m.types[s.type] || 0) + 1;
    }
    return map;
  }, [shifts]);

  const prevWeek = () => { const d = new Date(anchor); d.setDate(d.getDate() - 7); setAnchor(d); };
  const nextWeek = () => { const d = new Date(anchor); d.setDate(d.getDate() + 7); setAnchor(d); };

  const weekLabel = `${days[0].toLocaleDateString([], { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" })}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="label-overline">Week of</span>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-1">{weekLabel}</h3>
        </div>
        <div className="flex gap-2">
          {isManager && (
            <>
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-assign-shifts">
                <Users className="w-4 h-4 mr-1" /> Bulk assign
              </Button>
              <Button variant="outline" size="sm" onClick={copyPrevWeek} data-testid="copy-prev-week">
                <CopyPlus className="w-4 h-4 mr-1" /> Copy last week
              </Button>
              <Button variant="outline" size="sm" onClick={() => setTplOpen(true)} data-testid="manage-templates">
                <Settings2 className="w-4 h-4 mr-1" /> Manage templates
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={prevWeek} data-testid="shift-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="shift-today">Today</Button>
          <Button variant="outline" size="sm" onClick={nextWeek} data-testid="shift-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Template legend */}
      <div className="flex flex-wrap gap-2 text-xs">
        {templates.map((t) => {
          const c = COLOR_CLASSES[t.color] || COLOR_CLASSES.zinc;
          return (
            <span key={t.key} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border ${c.bg} ${c.text} ${c.border}`}>
              <span className={`w-2 h-2 rounded-full ${c.swatch}`} />
              <span className="font-semibold">{t.name}</span>
              <span className="font-mono text-[10px] opacity-70">{t.start_time}–{t.end_time}</span>
            </span>
          );
        })}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="text-sm min-w-full">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left py-2.5 px-3 sticky left-0 bg-zinc-50 min-w-[180px] uppercase text-[10px] tracking-widest text-zinc-500">Staff</th>
              {days.map((d) => (
                <th key={d.toISOString()} className={`py-2 px-2 text-center font-medium uppercase text-[10px] tracking-widest min-w-[120px] ${fmtDate(d) === fmtDate(new Date()) ? "text-zinc-900" : "text-zinc-500"}`}>
                  <div>{d.toLocaleString([], { weekday: "short" })}</div>
                  <div className="text-zinc-400 font-mono text-[11px]">{d.getDate()}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100" data-testid={`shift-row-${u.id}`}>
                <td className="py-2 px-3 sticky left-0 bg-white whitespace-nowrap">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[10px] text-zinc-500 capitalize">{u.role.replace("_", " ")}</div>
                </td>
                {days.map((d) => {
                  const s = shifts.find((x) => x.user_id === u.id && x.date === fmtDate(d));
                  const tpl = s ? templateByKey[s.type] : null;
                  const color = tpl?.color || "zinc";
                  const c = COLOR_CLASSES[color] || COLOR_CLASSES.zinc;
                  return (
                    <td
                      key={fmtDate(d)}
                      onClick={() => openCell(u, d)}
                      className={`py-1.5 px-1.5 text-center ${isManager ? "cursor-pointer hover:bg-zinc-50" : ""}`}
                      data-testid={`shift-cell-${u.id}-${fmtDate(d)}`}
                    >
                      {s ? (
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded ${c.bg} ${c.text} border ${c.border}`}>
                          <span className="text-[10px] font-semibold uppercase tracking-wide">{tpl?.name || s.type}</span>
                        </div>
                      ) : (
                        <span className="text-zinc-300 text-[10px]">{isManager ? "+ Assign" : "—"}</span>
                      )}
                      {s && (
                        <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{s.start_time}–{s.end_time}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={days.length + 1} className="py-10 text-center text-zinc-500">No staff.</td></tr>
            )}
          </tbody>
          {isManager && (
            <tfoot className="bg-zinc-50 border-t-2 border-zinc-200">
              <tr>
                <td className="py-2 px-3 sticky left-0 bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Coverage</td>
                {days.map((d) => {
                  const cov = coverageByDay[fmtDate(d)];
                  return (
                    <td key={fmtDate(d)} className="py-2 px-1.5 text-center align-top" data-testid={`coverage-foot-${fmtDate(d)}`}>
                      {cov ? (
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-sm font-bold text-zinc-900">{cov.total}</span>
                          <div className="flex flex-wrap justify-center gap-0.5">
                            {Object.entries(cov.types).map(([type, n]) => {
                              const c = COLOR_CLASSES[templateByKey[type]?.color] || COLOR_CLASSES.zinc;
                              return (
                                <span key={type} className={`inline-flex items-center ${c.bg} ${c.text} rounded px-1 text-[9px] font-semibold`} title={templateByKey[type]?.name || type}>
                                  {(templateByKey[type]?.name || type).slice(0, 1).toUpperCase()}{n}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <span className="text-zinc-300 text-xs">0</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Assignment dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Shift · {form.user_name} · {form.date}</DialogTitle>
            <DialogDescription>Assign a shift template or set custom start and end times.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Shift template</Label>
              <Select value={form.type} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="shift-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.name} ({t.start_time}–{t.end_time})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label>Start</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="shift-start" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="shift-end" />
              </div>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Reception desk" />
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <div>
              {form.existing_id && (
                <Button variant="outline" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="shift-delete">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              )}
            </div>
            <Button onClick={submit} className="bg-zinc-950 hover:bg-zinc-800" data-testid="shift-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShiftTemplatesDialog open={tplOpen} onOpenChange={setTplOpen} templates={templates} onChange={load} />

      <BulkShiftAssignDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        users={users}
        templates={templates}
        defaultDateFrom={dateFrom}
        defaultDateTo={dateTo}
        onDone={load}
      />
    </div>
  );
}

function ShiftTemplatesDialog({ open, onOpenChange, templates, onChange }) {
  const empty = { key: "", name: "", start_time: "09:00", end_time: "17:00", color: "blue", description: "", _editing: false };
  const [form, setForm] = useState(empty);

  const startNew = () => setForm({ ...empty });
  const startEdit = (t) => setForm({ key: t.key, name: t.name, start_time: t.start_time, end_time: t.end_time, color: t.color || "blue", description: t.description || "", _editing: true, _is_system: t.is_system });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (form._editing) {
        const { name, start_time, end_time, color, description } = form;
        await api.put(`/shift-templates/${form.key}`, { name, start_time, end_time, color, description });
        toast.success("Template updated");
      } else {
        if (!form.key.trim()) { toast.error("Key is required"); return; }
        await api.post(`/shift-templates`, { key: form.key, name: form.name, start_time: form.start_time, end_time: form.end_time, color: form.color, description: form.description });
        toast.success("Template created");
      }
      setForm(empty);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete shift template "${t.name}"?`)) return;
    try {
      await api.delete(`/shift-templates/${t.key}`);
      toast.success("Template deleted");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Manage Shift Templates</DialogTitle></DialogHeader>

        <div className="space-y-2 max-h-64 overflow-auto" data-testid="templates-list">
          {templates.map((t) => {
            const c = COLOR_CLASSES[t.color] || COLOR_CLASSES.zinc;
            return (
              <div key={t.key} className="flex items-center justify-between border border-zinc-200 rounded-md p-3" data-testid={`template-row-${t.key}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ${c.swatch}`} />
                  <div>
                    <div className="font-medium text-sm">{t.name}{t.is_system && <span className="ml-2 text-[10px] text-zinc-400 uppercase tracking-widest">system</span>}</div>
                    <div className="text-xs text-zinc-500 font-mono">{t.key} · {t.start_time}–{t.end_time}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(t)} className="p-2 hover:bg-zinc-100 rounded" data-testid={`edit-template-${t.key}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {!t.is_system && (
                    <button onClick={() => remove(t)} className="p-2 hover:bg-red-50 hover:text-red-600 rounded" data-testid={`delete-template-${t.key}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-zinc-200 pt-4 mt-2">
          <div className="flex items-center justify-between mb-3">
            <span className="label-overline">{form._editing ? `Editing ${form.name || form.key}` : "Add new template"}</span>
            {form._editing && (
              <button type="button" onClick={startNew} className="text-xs text-zinc-500 hover:text-zinc-900">Cancel edit</button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Front Desk Morning" data-testid="tpl-name" />
            </div>
            <div>
              <Label>Key (slug)</Label>
              <Input
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                placeholder="front_desk_morning"
                disabled={form._editing}
                data-testid="tpl-key"
              />
            </div>
            <div>
              <Label>Start time</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="tpl-start" />
            </div>
            <div>
              <Label>End time</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="tpl-end" />
            </div>
            <div className="sm:col-span-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded-full ${COLOR_CLASSES[c].swatch} ring-offset-1 transition-all ${form.color === c ? "ring-2 ring-zinc-900" : "hover:scale-105"}`}
                    title={c}
                    data-testid={`tpl-color-${c}`}
                  />
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label>Description (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="When this shift is used..." />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={save} className="bg-zinc-950 hover:bg-zinc-800" data-testid="tpl-save">
            {form._editing ? "Save changes" : <><Plus className="w-4 h-4 mr-1" /> Create template</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
